<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.pointRewardList.modal.new.buttonText')"
      @click="openPointRewardModal = true"
    />
    <export
      url="/point_rewards/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.pointRewards')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <point-reward-modal
    v-model:open="openPointRewardModal"
    v-model:point-reward="editedPointReward"
    @saved="refetch()"
  />
  <div class="main-area h-full">
    <div class="ml-12">
      <div class="text-body-regular-bold text-table-text-gray uppercase mb-5">
        {{ $t('pages.pointRewardList.tableHeader') }}
      </div>
      <div class="table">
        <data-table
          v-model:sort-by="sortKey"
          v-model:sort-direction="sortDirection"
          class="mb-auto"
          :busy="loading"
          :items="records"
          :columns="columns"
          :visible-columns="visibleColumns"
          :disable-frontend-sorting="true"
          :actions="actions"
          :row-link="(item) => ({ name: 'point-reward-details', params: { pointRewardId: item?.id ?? '-' } })"
        />
      </div>
    </div>
  </div>
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
  />
</template>

<script lang="ts" setup>
import {
  computed, inject, reactive, ref, unref, watch,
  ref as vueRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { useProvider } from '@utils/table/hooks/provider';
import Action from '@utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg';

import type { Filter } from '@utils/filter/Filter.vue';
import { useDelete } from '@hooks/api';
import Tabs from '@components/structure/navigation/Tabs.vue';
import PointRewardModal from '@pages/loyalty/PointRewardModal.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import { format } from 'date-fns';
import { useTableFields } from '@hooks/table-configuration';
import { useQueryRef } from '@hooks/url';
import GlobalRegionFilter from '@utils/globalFilter/GlobalRegionFilter.vue';
import globalFilterKey from '@utils/globalFilter/injects';
import type {
  CampaignGroup, HydraCollection, PointReward, Region,
} from '@/entities/Entities';
import { syncHeader } from '@/components/structure/navigation/injects';

interface Props {
  modelValue: CampaignGroup;
}

const props = defineProps<Props>();

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 100);
const filter = useQueryRef('filter', {});

const globalFilter = inject(globalFilterKey, vueRef({ regions: [] }));
const globalFilterValue = globalFilter;

const fixedFilter = computed(() => ({
  group: props.modelValue?.id ?? '',
  'inArray[regions]': globalFilter?.value?.regions,
}));

const totalFilter = computed(() => ({
  ...fixedFilter.value,
  ...filter.value,
}));

const openPointRewardModal = ref(false);
const editedPointReward = ref();
const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);
const { t } = useI18n();

const [deletePointReward, { loading: deleting }] = useDelete(
  t('pages.pointRewardList.actions.deleteWarning'),
);

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const actions = [
  {
    title: t('pages.pointRewardList.actions.edit'),
    action: (item: PointReward) => {
      openPointRewardModal.value = true;
      editedPointReward.value = { ...item };
    },
  },
  {
    title: t('pages.pointRewardList.actions.delete'),
    action: async (item: PointReward) => {
      if (await deletePointReward(`/point_rewards/${item.id}`)) {
        refetch();
      }
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: false,
    group: t('pages.pointRewardList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    filter: {
      type: 'text',
      group: t('pages.pointRewardList.filterGroups.general'),
      options: {
        label: t('id'),
      },
    },
  },
  {
    key: 'name',
    title: t('pages.pointReward.name'),
    group: t('pages.pointRewardList.fieldGroups.general'),
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.pointRewardList.filterGroups.general'),
      options: {
        label: t('pages.pointReward.name'),
      },
    },
  },
  {
    key: 'active',
    type: 'bool',
    title: t('pages.pointReward.active.title'),
    group: t('pages.pointRewardList.fieldGroups.general'),
    options: {
      labelTrue: t('pages.pointReward.active.yes'),
      labelFalse: t('pages.pointReward.active.no'),
    },
    formatter: (v: boolean) => (v ? t('pages.pointReward.active.yes') : t('pages.pointReward.active.no')),
    filter: {
      type: 'bool',
      group: t('pages.pointRewardList.filterGroups.general'),
      options: {
        label: t('pages.pointReward.active.title'),
      },
    },
  },
  {
    key: 'cost',
    title: t('pages.pointReward.cost'),
    group: t('pages.pointRewardList.fieldGroups.general'),
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.pointRewardList.filterGroups.general'),
      options: {
        label: t('pages.pointReward.cost'),
      },
    },
  },
  {
    key: 'validityStart',
    title: t('pages.pointReward.validityStart'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.pointRewardList.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.pointRewardList.filterGroups.general'),
      options: {
        label: t('pages.pointReward.validityStart'),
      },
    },
  },
  {
    key: 'validityEnd',
    title: t('pages.pointReward.validityEnd'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.pointRewardList.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.pointRewardList.filterGroups.general'),
      options: {
        label: t('pages.pointReward.validityEnd'),
      },
    },
  },
  {
    key: 'availableStock',
    title: t('pages.pointReward.initialStock'),
    group: t('pages.pointRewardList.fieldGroups.general'),
    visible: true,
    formatter: (v: number | null) => (v === null ? t('pages.pointReward.unlimitedStock') : v),
    filter: {
      type: 'range',
      group: t('pages.pointRewardList.filterGroups.general'),
      options: {
        label: t('pages.pointReward.initialStock'),
      },
    },
  },
  {
    key: 'userQuota',
    title: t('pages.pointReward.userQuota'),
    group: t('pages.pointRewardList.fieldGroups.general'),
    visible: true,
    formatter: (v: number | null) => (v === null ? t('pages.pointReward.unlimitedStock') : v),
    filter: {
      type: 'range',
      group: t('pages.pointRewardList.filterGroups.general'),
      options: {
        label: t('pages.pointReward.userQuota'),
      },
    },
  },
  {
    key: 'regions',
    title: t('pages.pointReward.regions'),
    group: t('pages.pointRewardList.fieldGroups.general'),
    visible: true,
    formatter: (v: Region[]) => v.map((r) => r.name)
      .join(', '),
  },
  {
    key: 'orderIndex',
    title: t('pages.pointReward.orderIndex.title'),
    group: t('pages.pointRewardList.fieldGroups.general'),
  },
  // TODO: check translations? "Promoted" -> "Beworben"?
  {
    key: 'promoted',
    type: 'bool',
    title: t('pages.pointReward.promoted.title'),
    group: t('pages.pointRewardList.fieldGroups.general'),
    options: {
      labelTrue: t('pages.pointReward.promoted.yes'),
      labelFalse: t('pages.pointReward.promoted.no'),
    },
    formatter: (v: boolean) => (v ? t('pages.pointReward.promoted.yes') : t('pages.pointReward.promoted.no')),
    filter: {
      type: 'bool',
      group: t('pages.pointRewardList.filterGroups.general'),
      options: {
        label: t('pages.pointReward.promoted.title'),
      },
    },
  },
];

const visibleColumns = useTableFields(columns);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<PointReward>>('/point_rewards', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));

const loading = computed(() => loadingTable.value || deleting.value);

const uniqueRegions = computed(() => {
  const regionMap: Record<string, { id: string; name: string }> = {};
  if (records.value) {
    records.value.forEach((reward: PointReward) => {
      if (reward.regions && Array.isArray(reward.regions)) {
        reward.regions.forEach((region: Region & { userAgentTranslation?: { title: string } }) => {
          if (!regionMap[region.id]) {
            regionMap[region.id] = {
              id: region.id,
              name: region.userAgentTranslation?.title || region.name,
            };
          }
        });
      }
    });
  }
  return Object.values(regionMap);
});

syncHeader(null, () => ({
  extraBreadcrumbs: [],
  globalFilterComponent: {
    component: GlobalRegionFilter,
    props: {
      campaignGroup: null,
      modelValue: globalFilterValue,
      regionsList: uniqueRegions,
    },
    events: {
      'update:model-value': (e: string[]) => {
        globalFilterValue.value = e;
      },
    },
  },
}));
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>

